import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  ViewChild,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { AmbientLight, HemisphereLight, LinearSRGBColorSpace, OrthographicCamera, PerspectiveCamera, Scene, WebGLRenderer } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import RenderingContext from '../../../../working-table/visualisation/rendering-context';
import { LoadGroup } from 'src/app/working-table/visualisation/group';
import { LoadsGroupsDrawer } from '../../../../working-table/visualisation/loads-groups-drawer';
import * as _ from 'lodash';
import { LoadingSpace } from '../../../../working-table/visualisation/loading-space';
import { LoadFactory } from '../../../../working-table/visualisation/loadFactory';
import { FormDataType } from '../../add-load.component';
import { Project } from '../../../../core/objects/project';
import { Dimensions } from 'src/app/core/types';

@Component({
    selector: 'gl-load-preview',
    templateUrl: './load-preview.component.html',
    styleUrls: ['./load-preview.component.scss'],
    standalone: false
})
export class LoadPreviewComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() isOrto = false;
  @Input() loadData: FormDataType;
  @Input() width: number;
  @Input() height: number;
  @Input() project: Project;
  @ViewChild('loadContainer')
  public visualisationContainer: ElementRef<HTMLElement>;
  private scene: Scene;
  private camera: PerspectiveCamera | OrthographicCamera;
  private renderer: WebGLRenderer;
  private controls: OrbitControls;
  private cameraFOV = 75;
  private renderingContext: RenderingContext<PerspectiveCamera | OrthographicCamera>;
  private animationID: number;

  constructor(private zone: NgZone) {}

  ngOnDestroy(): void {
    this.scene.remove(...this.scene.children);
    LoadFactory.clear();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
    this.renderingContext = null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.loadData && changes.loadData.currentValue) {
      this.addObjects();
    }
    if (!!changes.loadData && !changes.loadData.currentValue) {
      this.scene = new Scene();
      LoadFactory.clear();
    }
  }

  private animate = () => {
    if (this.renderer) {
      this.renderer.render(this.scene, this.camera);
      this.controls.update();
      this.animationID = requestAnimationFrame(this.animate);
    }
  };

  private calculateCameraPosition(objectLength: number): number {
    const fovRadians = this.cameraFOV * (Math.PI / 180);
    const distance = Math.abs(objectLength / Math.sin(fovRadians / 2)) + 5000;
    return distance;
  }

  private initializeOrto(): void {
    if (!this.visualisationContainer) {
      return;
    } else {
      this.zone.runOutsideAngular(() => {
        this.scene = new Scene();
        this.camera = new OrthographicCamera(
          this.width / -2,
          this.width / 2,
          this.height / 2,
          this.height / -2,
          -100000000,
          100000000,
        );
        this.renderer = new WebGLRenderer({
          antialias: true,
          alpha: true,
          preserveDrawingBuffer: true,
        });
        this.renderer.outputColorSpace = LinearSRGBColorSpace;
        this.renderer.setSize(this.width, this.height);
        this.visualisationContainer.nativeElement.appendChild(this.renderer.domElement);
        this.renderingContext = new RenderingContext(this.scene, this.renderer, this.camera);
        this.addLights();
        this.initializeControls();
        this.animate();
      });
    }
  }

  private initializePreview(): void {
    this.zone.runOutsideAngular(() => {
      this.scene = new Scene();
      this.camera = new PerspectiveCamera(75, this.width / this.height, 100, 1000000000000);
      this.renderer = new WebGLRenderer({
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
      });
      this.renderer.outputColorSpace = LinearSRGBColorSpace;
      this.renderer.setSize(this.width, this.height);
      this.visualisationContainer.nativeElement.appendChild(this.renderer.domElement);
      this.renderingContext = new RenderingContext(this.scene, this.renderer, this.camera);
      this.addLights();
      this.initializeControls();
    });
  }

  private addLights(): void {
    const hemisphereLight = new HemisphereLight(0xffffff, 0xffffff, 2.0);
    this.scene.add(hemisphereLight);
    const ambientLight = new AmbientLight(0xffffff, 0.8);
    this.scene.add(ambientLight);
  }

  private addObjects(): void {
    if (this.loadData === undefined) {
      return;
    }
    if (this.camera === undefined) {
      return;
    }
    const scalar = this.project.options.unit === 'lb' ? 100 * 2.54 : 100;
    const loadDimensions: Dimensions = {
      diameter: _.round(this.loadData.diameter * scalar, 2),
      width: _.round(this.loadData.width * scalar, 2),
      height: _.round(this.loadData.height * scalar, 2),
      length: _.round(this.loadData.length * scalar, 2),
    };

    if (this.loadData.loadType === 1) {
      loadDimensions.width = loadDimensions.width ? loadDimensions.width : loadDimensions.diameter;
      loadDimensions.length = loadDimensions.length
        ? loadDimensions.length
        : loadDimensions.diameter;
    } else if (this.loadData.loadType === 2) {
      loadDimensions.width =
        loadDimensions.diameter > loadDimensions.length
          ? loadDimensions.length
          : loadDimensions.diameter;
      loadDimensions.height = loadDimensions.height
        ? loadDimensions.height
        : loadDimensions.diameter;
    }
    this.scene = new Scene();
    this.addLights();
    LoadFactory.clear();
    if (this.renderingContext) {
      this.renderingContext.scene = this.scene;
      this.renderingContext.camera = this.camera;
    }
    const totalWidth = loadDimensions.width / 2;
    const totalHeight = loadDimensions.height / 2;
    const totalLength = loadDimensions.length / 2;
    const totalDiameter = loadDimensions.diameter / 2;

    this.scene.translateX(this.loadData.loadType === 2 ? -totalDiameter : -totalWidth);
    this.scene.translateY(-totalHeight);
    this.scene.translateZ(-totalLength);

    const loadingSpace: Partial<LoadingSpace> = {
      name: 'loadVisualization',
      length: loadDimensions.length,
      width: loadDimensions.width,
      height: loadDimensions.height,
      limit: 0,
      length1: null,
      width1: null,
      height1: null,
      limit1: null,
      set: false,
      type: 1,
      axis: [],
      dimensionsOfCabin: null,
    };
    const loadName = this.loadData?.name ? this.loadData.name : '';
    const loadGroup = new LoadGroup(
      0,
      this.loadData.loadType,
      this.loadData.color,
      loadName,
      0,
      0,
      null,
      0,
      0,
      loadDimensions,
      { x: 0, y: 0, z: 0 },
      loadDimensions,
      { x: 1, y: 1, z: 1 },
      {
        stacking: false,
        rotated: false,
        flipped: false,
        dragged: false,
      },
      0,
      0,
      loadName,
      true,
      this.loadData.centerOfMass,
    );
    const loadGroups = [loadGroup];
    const loadsGroupsDrawer = new LoadsGroupsDrawer(false, loadingSpace);
    const camPos = 9999;
    cancelAnimationFrame(this.animationID);
    if (!this.isOrto && this.camera && this.camera.position) {
      const distance = this.calculateCameraPosition(
        Math.max(totalLength, totalHeight, totalWidth, totalDiameter),
      );
      this.camera.position.set(distance, camPos, camPos);
    } else if (this.isOrto && this.camera && this.camera.position) {
      this.camera.zoom =
        Math.min(this.width / loadDimensions.length, this.height / loadDimensions.height) * 0.0035;
      this.camera.position.set(100, camPos, camPos);
    }
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrix();
    loadsGroupsDrawer.drawGroups(
      loadGroups,
      this.renderingContext.scene,
      this.project.options.unit,
      false,
      true,
    );
    this.animate();
    setTimeout(() => cancelAnimationFrame(this.animationID), 100);
  }

  private initializeControls(): void {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = false;
    this.controls.screenSpacePanning = false;
    this.controls.minDistance = 10;
    this.controls.maxDistance = 200000;
    this.controls.maxPolarAngle = Math.PI;
    if (this.isOrto) {
      this.controls.enablePan = false;
      this.controls.enableRotate = false;
    }
  }

  ngAfterViewInit(): void {
    this.visualisationContainer.nativeElement.style.setProperty('height', `${this.height}`);
    this.visualisationContainer.nativeElement.style.setProperty('width', `${this.width}`);
    if (!this.isOrto) {
      this.initializePreview();
      this.addObjects();
    } else {
      this.initializeOrto();
    }
  }
}
